import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlassPage } from '../components/chrome';
import { PageHero } from '../components/ui';
import { postJSON } from '../lib/api';

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organizationType: '',
    subject: location.state?.subject || '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await postJSON('/contact', {
        name: formData.name,
        email: formData.email,
        organizationType: formData.organizationType || 'N/A',
        subject: formData.subject || 'Website contact',
        message: formData.message,
      });
      setIsSubmitted(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', organizationType: '', subject: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error(err);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClass = 'glass-input w-full px-5 py-3.5 md:px-6 md:py-4';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-widest mb-2.5 text-zinc-500';

  return (
    <GlassPage>
      <PageHero
        title={<>Contact<br/>Us</>}
        lead="Have a question or want to get in touch? Send us a message and we'll get back to you as soon as possible."
      />

      <section className="px-4 md:px-6 pb-12 md:pb-16">
        <div className="glass max-w-4xl mx-auto px-5 sm:px-8 md:px-12 py-10 md:py-16">
          {isSubmitted ? (
            <div className="rounded-3xl border border-green-500/20 bg-green-500/10 p-8 text-center">
              <h3 className="text-2xl font-bold mb-4 text-green-700">Message Sent Successfully!</h3>
              <p className="text-zinc-600">Thank you for contacting us. We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Organization *</label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2318181b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1.25rem center'
                    }}
                  >
                    <option value="" disabled>Select type</option>
                    <option value="individual">Individual</option>
                    <option value="investor">Investor</option>
                    <option value="company">Company Representative</option>
                    <option value="partner">Potential Partner</option>
                    <option value="media">Media/Press</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="What is this about?"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={`${inputClass} resize-none`}
                  placeholder="Tell us what you'd like to discuss..."
                />
              </div>

              <button type="submit" disabled={isSubmitting} className={`btn-dark w-full md:w-auto md:px-16 ${isSubmitting ? 'opacity-60 cursor-wait' : ''}`}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitError && (
                <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </GlassPage>
  );
};

export default Contact;
